import React, {Component} from 'react';
import {Button, Card, Divider, Input} from "antd";
import {connect} from "react-redux";
import {compose} from "redux";
import {withTransparentLoading} from "./deferLoaderHoc";
import * as R from 'ramda';
import {
  loadBidComments,
  loadDailyLogComments, loadMeetingMinutesComments,
  loadProjectComments,
  loadPunchListComments,
  loadRfiComments,
  loadSubmittalComments,
  loadTaskComments,
  sendBidComment,
  sendDailyLogComment, sendMeetingMinutesComment,
  sendProjectComment,
  sendPunchComment,
  sendRfiComment,
  sendSubmittalComment,
  sendTaskComment
} from "../../actions/comments";
import UserSmall from "./UserSmall";
import PropTypes from 'prop-types';
import {dateSort} from "../../projections";

const { TextArea } = Input;

const mapMessages = messages => messages.map((comment, idx) =>
  <div key={idx} className='message'>
    <div className='message-text'>
      {comment.comment}
    </div>
    <div className='message-tagline'>
      <UserSmall id={comment.createdbyuserid}/> {comment.datecreated.fromNow()}
    </div>
  </div>
);

class ChatWall extends Component {
  constructor(props) {
    super(props);
    this.state = {
      sendMessage: ''
    };
    this.messages = React.createRef();
  }

  handleTyping = ({ target: { value }}) => {
    this.setState({ sendMessage: value });
  };

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (prevProps['messages'] !== this.props['messages']) {
      this.messages.scrollTop = this.messages.scrollHeight;
    }
  }

  handleSend = () => {
    const { onSend } = this.props;
    const { sendMessage } = this.state;
    const trimmedMessage = sendMessage.replace(/(\n\s*){2,}/g, '\n\n').trim();
    if (!trimmedMessage) null; // TODO: Handle validation error
    if (onSend) onSend(trimmedMessage);
    this.setState({ sendMessage: '' });
  };

  render() {
    let data = R.values(R.view(R.lensPath(['messages', 'data']), this.props)) || [];
    data = data.sort(dateSort(['datecreated'], false));
    const groupedData = R.groupBy(message => message.datecreated.clone().startOf('day'), data);
    const { sendMessage } = this.state;
    return (
      <Card className='chat-wall' title={<span>Notification Wall</span>}>
        <div className='content' ref={(el) => this.messages = el}>
          {R.values(groupedData).map((messages, idx) => !idx
            ? mapMessages(messages)
            : <React.Fragment>
              <Divider><h6 className='color-primary-0'>{messages[0].datecreated.format('ddd, MMM D, YYYY')}</h6></Divider>
              {mapMessages(messages)}
            </React.Fragment>
          )}
        </div>
        <div className='controls'>
          <TextArea rows={2} onChange={this.handleTyping} value={sendMessage}/>
          <Button size='large' type='primary' icon='caret-right' onClick={this.handleSend}>
            <span className='hidden inline visible-xl'>Send</span>
          </Button>
        </div>
      </Card>
    );
  }
}

ChatWall.propTypes = {
  messages: PropTypes.object,
  onSend: PropTypes.func,
};

const mapPropsToLoaderAction = (props) => {
  const { projectId, bidId, meetingMinutesId, dailyLogId, punchId, rfiId, submittalId, taskId } = props;
  if (projectId && meetingMinutesId) return loadMeetingMinutesComments(projectId, meetingMinutesId);
  if (projectId && dailyLogId) return loadDailyLogComments(projectId, dailyLogId);
  if (projectId && punchId) return loadPunchListComments(projectId, punchId);
  if (projectId && rfiId) return loadRfiComments(projectId, rfiId);
  if (projectId && submittalId) return loadSubmittalComments(projectId, submittalId);
  if (projectId && taskId) return loadTaskComments(projectId, taskId);
  if (projectId) return loadProjectComments(projectId);
  if (bidId) return loadBidComments(bidId);
};

const mapPropsToSendAction = (props) => {
  const { projectId, bidId, meetingMinutesId, dailyLogId, punchId, rfiId, submittalId, taskId } = props;
  if (projectId && meetingMinutesId) return (comment) => sendMeetingMinutesComment(projectId, meetingMinutesId, comment);
  if (projectId && dailyLogId) return (comment) => sendDailyLogComment(projectId, dailyLogId, comment);
  if (projectId && punchId) return (comment) => sendPunchComment(projectId, punchId, comment);
  if (projectId && rfiId) return (comment) => sendRfiComment(projectId, rfiId, comment);
  if (projectId && submittalId) return (comment) => sendSubmittalComment(projectId, submittalId, comment);
  if (projectId && taskId) return (comment) => sendTaskComment(projectId, taskId, comment);
  if (projectId) return (comment) => sendProjectComment(projectId, comment);
  if (bidId) return (comment) => sendBidComment(bidId, comment);
};

const getMessageSelector = (props) => {
  const { projectId, bidId, meetingMinutesId, dailyLogId, punchId, rfiId, submittalId, taskId } = props;
  if (meetingMinutesId) return R.view(R.lensPath(['comments', 'data', 'meetingminutes', meetingMinutesId]));
  if (dailyLogId) return R.view(R.lensPath(['comments', 'data', 'dailylogs', dailyLogId]));
  if (punchId) return R.view(R.lensPath(['comments', 'data', 'punches', punchId]));
  if (rfiId) return R.view(R.lensPath(['comments', 'data', 'rfis', rfiId]));
  if (submittalId) return R.view(R.lensPath(['comments', 'data', 'submittals', submittalId]));
  if (taskId) return R.view(R.lensPath(['comments', 'data', 'tasks', taskId]));
  if (projectId) return R.view(R.lensPath(['comments', 'data', 'projects', projectId]));
  if (bidId) return R.view(R.lensPath(['comments', 'data', 'bids', bidId]));
};

export default compose(
  connect(
    (state, props) => {
      let selector = getMessageSelector(props) || (() => []);
      return {
        messages: selector(state),
      }
    },
    (dispatch, props) => {
      let action = mapPropsToLoaderAction(props);
      let sendAction = mapPropsToSendAction(props);
      let newProps = {};
      if (action) newProps.loader = () => dispatch(action);
      if (sendAction) newProps.onSend = (comment) => comment && dispatch(sendAction(comment));
      return newProps;
    }
  ),
  withTransparentLoading
)(ChatWall);



// WEBPACK FOOTER //
// ./src/components/Boilerplate/ChatWall.js